import {
  CHORD_PAIRS,
  VOICE_LEADING_IDIOMS,
  VOICE_LEADING_KEY_POOL,
  getVoicingPair,
  scoreVoicing,
} from '@tunasong/music-lib'
import type {
  ChordPair,
  VoiceLeadingIdiom,
  VoiceLeadingKey,
  VoiceLeadingScoreBreakdown,
} from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { VoicingPianoRoll } from './voicing-piano-roll.js'
import styles from './voice-leading-explore-panel.module.css'

export interface VoiceLeadingExploreSelection {
  pair: ChordPair
  musicKey: VoiceLeadingKey
  idiom: VoiceLeadingIdiom
}

export type VoiceLeadingVoicingSlot = 'smooth' | 'awkward'

export interface VoiceLeadingExplorePanelProps {
  pair: ChordPair
  musicKey: VoiceLeadingKey
  idiom: VoiceLeadingIdiom
  onChange: (selection: VoiceLeadingExploreSelection) => void
  onPlayVoicing: (slot: VoiceLeadingVoicingSlot) => void
}

const IDIOM_LABEL: Record<VoiceLeadingIdiom, string> = {
  classical: 'Classical',
  jazz: 'Jazz',
  pop: 'Pop',
}

const formatScore = (total: number): string => (total > 0 ? `+${total}` : `${total}`)

const COLUMN_LABELS: Record<ChordPair, readonly [string, string]> = {
  'I-V': ['I', 'V'],
  'I-IV': ['I', 'IV'],
  'V-I': ['V', 'I'],
  'IV-V': ['IV', 'V'],
}

/**
 * Voice-Leading Explore panel. Controlled by the host (pair / key / idiom +
 * onChange). Renders the three pickers, both voicings as `<VoicingPianoRoll>`s
 * with idiom-scored badges, a per-voicing motion breakdown, and a play button
 * per voicing (playback owned by the host via `onPlayVoicing`).
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §7
 */
export const VoiceLeadingExplorePanel: FC<VoiceLeadingExplorePanelProps> = props => {
  const { pair, musicKey, idiom, onChange, onPlayVoicing } = props
  const voicingPair = getVoicingPair(musicKey, pair)
  const labels = COLUMN_LABELS[pair]

  const smoothScore = scoreVoicing(voicingPair.smooth, { idiom, pair })
  const awkwardScore = scoreVoicing(voicingPair.awkward, { idiom, pair })

  const breakdownCopy = useTheoryCopy('vl-explore-feedback')

  const select = (next: Partial<VoiceLeadingExploreSelection>): void =>
    onChange({ pair, musicKey, idiom, ...next })

  return (
    <div className={styles.panel}>
      <div className={styles.pickers}>
        <fieldset className={styles.picker} aria-label="Chord pair">
          <legend className={styles.pickerLegend}>Chord pair</legend>
          <div className={styles.pickerButtons}>
            {CHORD_PAIRS.map(option => (
              <Button
                key={option}
                type="button"
                variant={pair === option ? 'contained' : 'outlined'}
                aria-pressed={pair === option}
                onClick={() => select({ pair: option })}
              >
                {option}
              </Button>
            ))}
          </div>
        </fieldset>

        <fieldset className={styles.picker} aria-label="Key">
          <legend className={styles.pickerLegend}>Key</legend>
          <div className={styles.pickerButtons}>
            {VOICE_LEADING_KEY_POOL.map(option => (
              <Button
                key={option}
                type="button"
                variant={musicKey === option ? 'contained' : 'outlined'}
                aria-pressed={musicKey === option}
                onClick={() => select({ musicKey: option })}
              >
                {option}
              </Button>
            ))}
          </div>
        </fieldset>

        <fieldset className={styles.picker} aria-label="Idiom">
          <legend className={styles.pickerLegend}>Idiom</legend>
          <div className={styles.pickerButtons}>
            {VOICE_LEADING_IDIOMS.map(option => (
              <Button
                key={option}
                type="button"
                variant={idiom === option ? 'contained' : 'outlined'}
                aria-pressed={idiom === option}
                onClick={() => select({ idiom: option })}
              >
                {IDIOM_LABEL[option]}
              </Button>
            ))}
          </div>
        </fieldset>
      </div>

      <div className={styles.comparison}>
        <VoicingCard
          slot="smooth"
          title="Smoother voicing"
          columnLabels={labels}
          pitchVoicing={voicingPair.smooth}
          score={smoothScore}
          onPlay={() => onPlayVoicing('smooth')}
        />
        <VoicingCard
          slot="awkward"
          title="Awkward voicing"
          columnLabels={labels}
          pitchVoicing={voicingPair.awkward}
          score={awkwardScore}
          onPlay={() => onPlayVoicing('awkward')}
        />
      </div>

      {breakdownCopy?.body ? <p className={styles.breakdownCopy}>{breakdownCopy.body}</p> : null}
    </div>
  )
}

interface VoicingCardProps {
  slot: VoiceLeadingVoicingSlot
  title: string
  columnLabels: readonly [string, string]
  pitchVoicing: ReturnType<typeof getVoicingPair>['smooth']
  score: VoiceLeadingScoreBreakdown
  onPlay: () => void
}

const VoicingCard: FC<VoicingCardProps> = props => {
  const { slot, title, columnLabels, pitchVoicing, score, onPlay } = props
  return (
    <section className={styles.voicingCard} aria-label={title}>
      <header className={styles.voicingHeader}>
        <span className={styles.voicingTitle}>{title}</span>
        <span
          className={styles.scoreBadge}
          data-testid={`vl-score-badge-${slot}`}
          data-vl-score-badge=""
        >
          {formatScore(score.total)}
        </span>
      </header>
      <VoicingPianoRoll voicing={pitchVoicing} columnLabels={columnLabels} />
      <dl className={styles.breakdown}>
        <div className={styles.breakdownRow}>
          <dt>Common tones held</dt>
          <dd>{score.commonTonesHeld}</dd>
        </div>
        <div className={styles.breakdownRow}>
          <dt>Stepwise moves</dt>
          <dd>{score.stepwiseMoves}</dd>
        </div>
        <div className={styles.breakdownRow}>
          <dt>Leaps (inner / outer)</dt>
          <dd>
            {score.innerLeaps} / {score.outerLeaps}
          </dd>
        </div>
        <div className={styles.breakdownRow}>
          <dt>Parallel 5ths / 8vas</dt>
          <dd>{score.parallelFifthsOrOctaves}</dd>
        </div>
      </dl>
      <Button type="button" variant="outlined" onClick={onPlay} data-testid={`vl-play-${slot}`}>
        Play
      </Button>
    </section>
  )
}
